import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useArticleStore } from "@/stores/articleStore";
import { Calendar, User, Eye, EyeOff, ExternalLink } from "lucide-react";

interface ModalViewArticleProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ModalViewArticle({ isOpen, onClose }: ModalViewArticleProps) {
  const { selectedArticle, isLoadingDetail } = useArticleStore();

  if (!selectedArticle) return null;

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "published":
        return "default";
      case "draft":
        return "secondary";
      case "archived":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case "published":
        return "Đã xuất bản";
      case "draft":
        return "Bản nháp";
      case "archived":
        return "Đã lưu trữ";
      default:
        return status;
    }
  };

  // Get visibility label
  const getVisibilityLabel = (visibility: string) => {
    switch (visibility) {
      case "public":
        return "Công khai";
      case "private":
        return "Riêng tư";
      default:
        return visibility;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Chi tiết bài viết</span>
            <div className="flex gap-2">
              <Badge variant={getStatusBadgeVariant(selectedArticle.status)}>
                {getStatusLabel(selectedArticle.status)}
              </Badge>
              <Badge
                variant={
                  selectedArticle.visibility === "public"
                    ? "default"
                    : "secondary"
                }
              >
                {getVisibilityLabel(selectedArticle.visibility)}
              </Badge>
              {!selectedArticle.isDisplay && (
                <Badge variant="destructive">
                  <EyeOff className="w-3 h-3 mr-1" />
                  Ẩn
                </Badge>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {isLoadingDetail ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Article Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">
                {selectedArticle.title}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span>
                    Tác giả: {selectedArticle?.author?.name || "(Trống)"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Ngày tạo: {formatDate(selectedArticle.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Eye className="w-4 h-4" />
                  <span>Lượt xem: {selectedArticle.viewCount}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Slug: {selectedArticle.slug}</span>
                </div>
              </div>

              {selectedArticle.category && (
                <div className="mb-4">
                  <span className="text-sm font-medium">Thể loại: </span>
                  <Badge variant="outline">
                    {selectedArticle.category
                      .map((category) => category.name)
                      .join(", ")}
                  </Badge>
                </div>
              )}

              {selectedArticle.publishDate && (
                <div className="mb-4">
                  <span className="text-sm font-medium">Ngày xuất bản: </span>
                  <span className="text-sm text-gray-600">
                    {formatDate(selectedArticle.publishDate)}
                  </span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="bg-white p-4 rounded-lg border">
              <h3 className="font-semibold mb-3">Nội dung bài viết</h3>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: selectedArticle.content || "",
                }}
              />
            </div>

            {/* SEO Information */}
            {(selectedArticle.pageTitle ||
              selectedArticle.pageDescription ||
              selectedArticle.canonicalUrl ||
              selectedArticle.keywords?.length) && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Thông tin SEO</h3>
                <div className="space-y-3">
                  {selectedArticle.pageTitle && (
                    <div>
                      <span className="text-sm font-medium">
                        Tiêu đề trang:{" "}
                      </span>
                      <p className="text-sm text-gray-700">
                        {selectedArticle.pageTitle}
                      </p>
                    </div>
                  )}

                  {selectedArticle.pageDescription && (
                    <div>
                      <span className="text-sm font-medium">Mô tả trang: </span>
                      <p className="text-sm text-gray-700">
                        {selectedArticle.pageDescription}
                      </p>
                    </div>
                  )}

                  {selectedArticle.canonicalUrl && (
                    <div>
                      <span className="text-sm font-medium">
                        Canonical URL:{" "}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">
                          {selectedArticle.canonicalUrl}
                        </span>
                        <ExternalLink className="w-4 h-4 text-blue-600 cursor-pointer" />
                      </div>
                    </div>
                  )}

                  {selectedArticle.keywords &&
                    selectedArticle.keywords.length > 0 && (
                      <div>
                        <span className="text-sm font-medium">Keywords: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedArticle.keywords.map((keyword, index) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="text-xs"
                            >
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Featured Image */}
            {selectedArticle.featuredImage && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Hình đại diện</h3>
                <div className="flex items-center gap-3">
                  <img
                    src={selectedArticle.featuredImage.url}
                    alt={selectedArticle.featuredImage.alt}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div>
                    <p className="text-sm font-medium">
                      {selectedArticle.featuredImage.title}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedArticle.featuredImage.alt}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Movie Information */}
            {selectedArticle.movie && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Thông tin phim</h3>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium">
                      {selectedArticle.movie.title}
                    </p>
                    {selectedArticle.movie.category && (
                      <p className="text-sm text-gray-600">
                        Thể loại: {selectedArticle.movie.category.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actors and Directors */}
            {(selectedArticle.actors?.length ||
              0 > 0 ||
              selectedArticle.directors?.length ||
              0 > 0) && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Diễn viên & Đạo diễn</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedArticle.actors?.length ||
                    (0 > 0 && (
                      <div>
                        <span className="text-sm font-medium">Diễn viên: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedArticle.actors?.map((actor, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-xs"
                            >
                              {actor.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}

                  {selectedArticle.directors?.length ||
                    (0 > 0 && (
                      <div>
                        <span className="text-sm font-medium">Đạo diễn: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedArticle.directors?.map((director, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-xs"
                            >
                              {director.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
